/// <reference path="../../types.d.ts" />

import classnames from 'classnames';
import React from 'react';
import {
  DataGrid,
  Pagination,
  LoadingSlugs,
  SearchBar,
  Card,
  SelectList
} from '@codametrix/ui-components';

import { DatePicker } from '../../components/date-picker/datepicker';

import { CMxComponents } from '@codametrix/ui-components/src/types';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { defaultSortableOption } from '../../core/pageable-options';
import { defaultCriteriaFilter } from '../../core/criteria-filter';

import '../../styles/grid-common.scss';
import '../../styles/cmx.scss';
import './pageable-list.scss';
import { PopUpFiltersV2 } from '../pop-up-filter/pop-up-filtersV2';
import { AmplifyCore, commonEnums as enums } from '@codametrix/ui-common';
import { LOAD_STATES } from '../../reducers/common';
import classNames from 'classnames';

const { buildSortableWithFilters } = AmplifyCore.paginationUtils;
const { FilterTypes } = enums;

type PageableListState = {
  filterFormOpen: boolean;
  selectedForm: number | undefined;
  /** Local copy after sort so DataGrid defaultSort updates without mutating props */
  sortColumnDefs: CMxComponents.ColumnDef<any>[] | null;
};

const pageSizeOptions: CMxComponents.SelectListOption[] = [
  {
    label: '25',
    value: 25
  },
  {
    label: '50',
    value: 50
  },
  {
    label: '100',
    value: 100
  },
  {
    label: '200',
    value: 200
  }
];

export const dummy = () => {
  return DatePicker;
};

class PageableList<T> extends React.Component<
  AppProps.PageableListProperties<T>,
  PageableListState
> {
  static defaultProps = {
    list: (options: CMxCommonApp.SortablePageable<any>) => {},
    onNavigate: () => {},
    getDictionary: (dictionaryName: AppProps.CategoryParams) => {},
    getStatuses: (dictionaryName: AppProps.CategoryParams) => {},
    onAction: (content: any) => {},
    clearAllFilters: () => {},
    pageableOptions: defaultSortableOption,
    criteriaFilter: defaultCriteriaFilter,
    criteriaLoadState: LOAD_STATES.done
  };

  constructor(props: any) {
    super(props);
    this.state = {
      filterFormOpen: false,
      selectedForm: undefined,
      sortColumnDefs: null
    };
    this._handleFilterRemove = this._handleFilterRemove.bind(this);
    this._handleSearch = this._handleSearch.bind(this);
  }

  componentDidUpdate(prevProps: AppProps.PageableListProperties<T>) {
    if (prevProps.columnDefs !== this.props.columnDefs) {
      this.setState({ sortColumnDefs: null });
    }
  }

  componentDidMount() {
    const url = new URL(document.location.toString());
    if (!url.searchParams.has('back')) {
      this.props.list({ ...this.props.sortablePageable });
    } else {
      this.props.onNavigate();
    }
  }

  _handleSearch(event: CustomEvent) {
    const {
      detail: {
        value: { value, key }
      }
    } = event;

    let filters: CMxCommonApp.Filter[] = [];

    filters = this.props.criteriaFilter.filters.filter(filter => {
      return filter.type === FilterTypes.IN;
    });

    const SearchFormMatch = this.props.criteriaFilter.searchForms.find(
      filter => {
        return filter.key === key;
      }
    );

    //Skip Empty Searches
    //Ensure we have a matching search form
    //Ensure value from the custom event is a string
    if (
      value !== '' &&
      SearchFormMatch &&
      (typeof value === 'string' || value instanceof String)
    ) {
      let newFilter: CMxCommonApp.Filter = {
        key: key,
        terms: [value.trim()],
        type: SearchFormMatch.type
      };
      filters.push(newFilter);
    }

    this.props.list(
      buildSortableWithFilters(this.props.sortablePageable, {
        filters,
        dateFilter: undefined
      })
    );
  }

  _handleFilterRemove(event: React.MouseEvent) {
    event.preventDefault();
    const target = event.target;
    const parentChip = (target as HTMLElement)?.closest('.chip');
    const chipClose = (target as HTMLElement)?.closest('.chip-close');
    if (parentChip === null || chipClose === null) {
      return;
    }
    const filterKey = parentChip.getAttribute('id');

    const currentTerm = chipClose.getAttribute('id');

    const currentFilter = this.props.criteriaFilter.filters.find(filter => {
      return filter.key === filterKey;
    });

    if (!currentFilter) {
      return;
    }

    const dateFilter = this.props.sortablePageable.filterableOptions?.dateFilter;
    let nextFilters: CMxCommonApp.Filter[];

    if (currentFilter.terms.length === 1) {
      nextFilters = this.props.criteriaFilter.filters.filter(filter => {
        return filter.key !== filterKey;
      });
    } else {
      nextFilters = this.props.criteriaFilter.filters.map(filter => {
        if (filter.key !== filterKey) {
          return filter;
        }
        const newTerms = filter.terms.filter(term => term !== currentTerm);
        return { ...filter, terms: newTerms };
      });
    }

    this.props.list(
      buildSortableWithFilters(this.props.sortablePageable, {
        filters: nextFilters,
        dateFilter
      })
    );
  }

  render() {
    const { props } = this;
    const {
      loadState,
      filterLoadStates,
      items,
      columnDefs,
      onSelection,
      itemName,
      sortablePageable,
      criteriaFilter,
      idField,
      children,
      criteriaLoadState,
      onAction
    } = props;

    const displayColumnDefs =
      this.state.sortColumnDefs ?? columnDefs;

    const searchForms = criteriaFilter.searchForms.map(filterForm => {
      const isActiveSearch = criteriaFilter.filters.some(filter => {
        return filter.key === filterForm.key;
      });
      return {
        ...filterForm,
        isSelected: isActiveSearch
      };
    });

    const inFlight = loadState.loading || loadState.initial;
    const classes = [
      'pageable-list-view',
      'layout-full',
      { 'request-in-flight': inFlight }
    ];

    const itemCountClasses = [
      { 'request-in-flight': inFlight },
      'showing',
      'qa-item-count'
    ];
    const { pageableDefinition: pageable } = sortablePageable;
    const offsetInfo = pageable.pageable;
    let offset;

    if (pageable.totalElements) {
      offset = {
        start: offsetInfo.offset + 1,
        end: Math.min(
          offsetInfo.offset + offsetInfo.pageSize,
          pageable.totalElements
        )
      };
    } else {
      offset = {
        start: 0,
        end: 0
      };
    }

    const onPaginationClick = (action: CMxComponents.PaginationAction) => {
      if (!inFlight) {
        const {
          sortablePageable: {
            pageableDefinition: pageable,
            sortableOptions,
            filterableOptions
          }
        } = this.props;

        const retAction: CMxCommonApp.SortablePageable<any> = {
          pageableDefinition: {
            ...pageable,
            pageable: {
              ...pageable.pageable,
              pageNumber: action.index,
              pageSize: action.size
            }
          },
          sortableOptions,
          filterableOptions
        };
        this.props.list(retAction);
      }
    };

    const _handlePageResize = (event: CustomEvent) => {
      const pageAction: CMxComponents.PaginationAction = {
        index: 0,
        size: event.detail.value
      };
      onPaginationClick(pageAction);
    };

    const onSortClick = (action: CMxComponents.SortAction) => {
      if (!inFlight) {
        const {
          sortablePageable: {
            pageableDefinition: pageable,
            sortableOptions,
            filterableOptions
          },
          columnDefs: defsFromProps
        } = this.props;

        const sortableKeys =
          defsFromProps.find(def => def.key === action.key)?.sortableKeys ?? [];

        let nextSortableOptions = sortableOptions.map(o => ({ ...o }));

        sortableKeys.forEach(sortableKey => {
          const match = nextSortableOptions.find(
            option => option.sortField === sortableKey
          );

          if (match !== undefined) {
            if (action.direction === 'none') {
              nextSortableOptions = nextSortableOptions.filter(
                option => option.sortField !== match.sortField
              );
            } else if (
              action.direction === 'asc' ||
              action.direction === 'desc'
            ) {
              nextSortableOptions = nextSortableOptions.map(option =>
                option.sortField === match.sortField
                  ? { ...option, sortDirection: action.direction }
                  : option
              );
            }
          } else if (
            action.direction === 'asc' ||
            action.direction === 'desc'
          ) {
            nextSortableOptions = [
              ...nextSortableOptions,
              {
                sortField: sortableKey,
                sortDirection: action.direction
              }
            ];
          }
        });

        const colIndex = defsFromProps.findIndex(
          column => column.key === action.key
        );
        const nextColumnDefs = defsFromProps.map((col, i) =>
          i === colIndex ? { ...col, defaultSort: action.direction } : col
        );
        this.setState({ sortColumnDefs: nextColumnDefs });

        const retAction: CMxCommonApp.SortablePageable<any> = {
          pageableDefinition: {
            ...pageable
          },
          sortableOptions: nextSortableOptions,
          filterableOptions
        };
        this.props.list(retAction);
      }
    };

    const inner = loadState.initial ? (
      <Card className="loading-card" data-testid="loading-slugs">
        <LoadingSlugs numberItems={3} />
      </Card>
    ) : items.length ? (
      <DataGrid
        onRowSelection={onSelection}
        onSortSelection={onSortClick}
        columnDefs={displayColumnDefs}
        items={items}
        idField={idField || 'id'}
        onAction={onAction}
        data-testid="data-grid"
      />
    ) : (
      <h4
        className="content-vacant qa-content-vacant"
        data-testid="content-vacant">
        {' '}
        No {itemName} found
      </h4>
    );

    var defaultInput = sortablePageable.filterableOptions.filters?.find(
      filter => {
        return criteriaFilter.searchForms.find(form => filter.key === form.key);
      }
    )?.terms[0];
    return (
      <div className={classnames(classes)}>
        <div className="container heading-row layout-inner-header">
          {criteriaFilter.searchForms.length ||
          criteriaFilter.filterForms.length ? (
            <Card className={'header-card'}>
              {criteriaLoadState === LOAD_STATES.done ? (
                <span>
                  {criteriaFilter.searchForms?.length ? (
                    <div>
                      <div>
                        <SearchBar
                          FieldDefinitions={searchForms}
                          placeholder={'Search'}
                          defaultInput={defaultInput}
                          onSubmit={this._handleSearch}></SearchBar>
                      </div>
                    </div>
                  ) : (
                    false
                  )}

                  {criteriaFilter.filterForms?.length ? (
                    <div>
                      <div className="divider"></div>

                      <div>
                        <PopUpFiltersV2
                          criteriaFilter={criteriaFilter}
                          loadStates={filterLoadStates}
                          sortablePageable={this.props.sortablePageable}
                          list={this.props.list}
                          getDictionary={this.props.getDictionary}
                          getStatuses={this.props.getStatuses}
                          getTenants={this.props.getTenants}
                          clearFilters={this.props.clearFilters}
                          activeOrgId={this.props.activeOrgId}
                        />
                      </div>
                    </div>
                  ) : (
                    false
                  )}
                </span>
              ) : (
                <div className="criteria-loading qa-criteria-loading">
                  <LoadingSlugs numberItems={1} />
                </div>
              )}
            </Card>
          ) : (
            false
          )}
        </div>

        <div className="pageable-gutter">
          <span
            className={classNames(itemCountClasses)}
            data-testid="item-count">
            {offset.start}&ndash;{offset.end}{' '}
            <span style={{ fontSize: 12 }}>OF&nbsp;</span>
            {pageable.totalElements}{' '}
            <span style={{ fontSize: 12 }}>{itemName.toUpperCase()}</span>
            <span className={'page-size-select'} style={{ fontSize: 12 }}>
              Items Per Page
            </span>
            <span className={'qa-page-size-select-list'}>
              <SelectList
                name={'Page Size'}
                options={pageSizeOptions}
                value={pageable.size.toString()}
                onChange={_handlePageResize}
              />
            </span>
          </span>

          <div className={'m-2'}>{children}</div>
        </div>

        <div className="layout-inner-body pageable-list-inner-body ">
          <SwitchTransition mode={'out-in'}>
            <CSSTransition
              classNames="switch-views"
              timeout={150}
              key={loadState.loading ? 'list-loading' : 'list-loaded'}>
              {inner}
            </CSSTransition>
          </SwitchTransition>

          <div className="pagination-controls">
            <Pagination pageable={pageable} onSelect={onPaginationClick} />
          </div>
        </div>
      </div>
    );
  }
}

export default PageableList;
